import { Controller } from "@hotwired/stimulus"

// Drives the recently-changed-files picker on the new-ingestion-run form.
// Wiring (see app/views/tools/ingestion_runs/new.html.erb + _file_options):
//   - <form data-controller="ingestion-picker">
//   - number input: data-ingestion-picker-target="daysBack"
//                   data-action="keydown.enter->ingestion-picker#reload"
//   - "Find files" <button type="button">: data-action="ingestion-picker#reload"
//   - <turbo-frame> for the list: data-ingestion-picker-target="frame"
//                   data-action="turbo:frame-load->ingestion-picker#frameLoaded"
//   - reload spinner: data-ingestion-picker-target="spinner" (+ spinnerText/spinnerDots)
//   - Run/Cancel container: data-ingestion-picker-target="runControls"
//   - live filter (shown only with a list): data-ingestion-picker-target="filterControls",
//                   with a text input target="filter" (action input->#filter)
//                   and a Clear button (action #clearFilter)
//   - "select all" checkbox: data-ingestion-picker-target="selectAll" (action change->#toggleAll)
//   - each file row <li>: data-ingestion-picker-target="fileRow" data-filter-name="<name>"
//   - each file checkbox: data-ingestion-picker-target="check"
//   - no-matches message: data-ingestion-picker-target="noMatches"
//
// The controller element is the form (outside the frame), so it survives
// frame reloads; Stimulus re-binds the targets inside the frame each time
// its content swaps.
export default class extends Controller {
  static targets = [
    "daysBack", "frame", "spinner", "spinnerText", "spinnerDots",
    "runControls", "selectAll", "check",
    "filterControls", "filter", "fileRow", "noMatches",
  ]
  static values = { url: String }

  disconnect() {
    this.#stopProgress()
  }

  // Run a lookup for the entered day window by pointing the turbo-frame at
  // file_options?days_back=N (Turbo fetches on src change). Triggered explicitly
  // by the "Find files" button or Enter in the field — not on keystroke/blur.
  // While the lookup runs: hide the Run/Cancel controls and the filter, clear
  // the old list so it visibly disappears, and show the spinner. frameLoaded
  // restores them.
  reload(event) {
    if (event) event.preventDefault()

    const days = parseInt(this.daysBackTarget.value, 10) || 5
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("days_back", days)

    if (this.hasRunControlsTarget) this.runControlsTarget.classList.add("d-none")
    if (this.hasFilterControlsTarget) this.filterControlsTarget.classList.add("d-none")
    if (this.hasFilterTarget) this.filterTarget.value = ""
    this.frameTarget.innerHTML = ""
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none")
    this.#startProgress(days)

    this.frameTarget.setAttribute("src", url.pathname + url.search)
  }

  // Once the frame's new content has arrived: stop the timer, hide the spinner,
  // and reveal the Run/Cancel controls and the filter only if the lookup
  // returned selectable files (not on an empty result or a Dropbox error).
  frameLoaded() {
    this.#stopProgress()
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("d-none")

    const hasFiles = !!this.frameTarget.querySelector('input[name="ingestion_run[files][]"]')
    if (this.hasRunControlsTarget) this.runControlsTarget.classList.toggle("d-none", !hasFiles)
    if (this.hasFilterControlsTarget) this.filterControlsTarget.classList.toggle("d-none", !hasFiles)

    // Fresh list → start unfiltered.
    if (this.hasFilterTarget) this.filterTarget.value = ""
    this.filter()
  }

  // Live, as-you-type filtering of the file list. Case-insensitive, and treats
  // spaces and hyphens as equivalent, so "Butyl ether" matches
  // "vanillyl-butyl-ether.html". Shows the no-matches note when nothing matches.
  filter() {
    const q = this.#normalize(this.hasFilterTarget ? this.filterTarget.value : "")
    let visible = 0

    this.fileRowTargets.forEach((row) => {
      const name = this.#normalize(row.dataset.filterName || "")
      const match = q === "" || name.includes(q)
      row.hidden = !match
      if (match) visible += 1
    })

    if (this.hasNoMatchesTarget) {
      this.noMatchesTarget.hidden = !(this.fileRowTargets.length > 0 && visible === 0)
    }
  }

  clearFilter() {
    if (!this.hasFilterTarget) return
    this.filterTarget.value = ""
    this.filter()
    this.filterTarget.focus()
  }

  // Lowercase; collapse any run of spaces/hyphens to a single space so the two
  // are interchangeable for matching.
  #normalize(str) {
    return String(str).toLowerCase().replace(/[\s-]+/g, " ").trim()
  }

  // Live status while a lookup runs: stage labels that mirror the server's
  // steps (auth -> list folder -> filter by date) plus a row of second-dots —
  // one green dot per completed second and a gray dot for the second currently
  // in progress.
  #startProgress(days) {
    if (!this.hasSpinnerTextTarget) return

    const started = performance.now()
    const dayLabel = `${days} ${days === 1 ? "day" : "days"}`
    const stages = [
      { until: 1200, text: "Connecting to Dropbox…" },
      { until: 4000, text: "Listing the converted-HTML folder…" },
      { until: Infinity, text: `Filtering to files changed in the last ${dayLabel}…` },
    ]
    const dot = (color) => `<span class="ms-1" style="color: ${color}">●</span>`

    const tick = () => {
      const elapsed = performance.now() - started
      const secs = Math.floor(elapsed / 1000)
      const stage = stages.find((s) => elapsed < s.until)
      this.spinnerTextTarget.textContent = stage.text

      if (this.hasSpinnerDotsTarget) {
        this.spinnerDotsTarget.innerHTML = dot("#adb5bd") + dot("#198754").repeat(secs)
        this.spinnerDotsTarget.setAttribute("aria-label", `${secs}s`)
      }
    }

    tick()
    this.progressTimer = setInterval(tick, 250)
  }

  #stopProgress() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
      this.progressTimer = null
    }
  }

  // "Select all" toggles only the currently-visible (unfiltered) file rows, so
  // a filtered-out file is never selected by surprise.
  toggleAll() {
    const on = this.selectAllTarget.checked
    this.checkTargets.forEach((cb) => {
      const row = cb.closest("li")
      if (!row || !row.hidden) cb.checked = on
    })
  }
}
